import React from 'react';

import { Box, Divider, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import type { RoomAssetLink } from '../packageOptions/types';

import EgsCategoryFieldsForm from './EgsCategoryFieldsForm';
import EgsRoomScopeControl from './EgsRoomScopeControl';
import type { EgsCategoryMeta, EgsGuestCategoryDraft } from './types';

interface Props {
  category: EgsGuestCategoryDraft;
  currency: string;
  currentRoom: string;
  locked: boolean;
  meta: EgsCategoryMeta;
  onChange: (changes: Partial<EgsGuestCategoryDraft>) => void;
  onToggleEnabled: () => void;
  rooms: Array<RoomAssetLink>;
}

export default function EgsCategoryColumn({
  category,
  currency,
  currentRoom,
  locked,
  meta,
  onChange,
  onToggleEnabled,
  rooms,
}: Props) {
  const enabled = category.enabled && !locked;

  return (
    <Paper
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: 1.5,
        minWidth: 0,
        // Dim only when locked - the resting "No" state must still look interactive.
        opacity: locked ? 0.5 : 1,
        p: 2,
      }}
      variant="outlined"
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Typography fontWeight={600} variant="subtitle2">
          {meta.label}
        </Typography>
        {locked ? (
          <Typography color="text.secondary" variant="caption">
            Already set
          </Typography>
        ) : (
          <ToggleButtonGroup
            exclusive
            onChange={(_event, nextValue: boolean | null) => {
              if (nextValue !== null && nextValue !== category.enabled) {
                onToggleEnabled();
              }
            }}
            size="small"
            value={category.enabled}
          >
            <ToggleButton value={false}>No</ToggleButton>
            <ToggleButton
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { backgroundColor: 'primary.dark' },
                },
              }}
              value
            >
              Yes
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      <Typography color="text.secondary" variant="caption">
        {meta.question}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3, opacity: enabled ? 1 : 0.5 }}>
        <EgsCategoryFieldsForm
          category={category}
          currency={currency}
          enabled={enabled}
          meta={meta}
          onChange={onChange}
        />

        <Divider />

        <EgsRoomScopeControl
          currentRoom={currentRoom}
          disabled={!enabled}
          mode={category.applyMode}
          onModeChange={(mode) => onChange({ applyMode: mode })}
          onRoomsChange={(applyRooms) => onChange({ applyRooms })}
          rooms={rooms}
          selectedRooms={category.applyRooms}
          title={`Set this ${meta.label.toLowerCase()} rule for`}
        />
      </Box>
    </Paper>
  );
}
